// USER-CODE-ORIGIN security review B1: the `moduleData` a board / thumbnail
// TILE may use. The tile runs a project's own code in an opaque frame. It must
// never get the shell-root `moduleData`: that bridge carries the ACTIVE
// workspace's MERGED app grants, and the SDK takes a `workspaceId` in the args
// over the active workspace — a frame-supplied id reached any workspace of
// the viewer.
//
// `tileModuleDataBridge(project)` (this = a shell element) returns a bridge
// built PER FRAME, at mount, by the host:
//   • only if THIS project is installed in the active workspace (the
//     takeover module or an additive app, matched by owner/key);
//   • from THAT install's own grant only (records), never the union of apps;
//   • pinned to that workspace id — the host sets `workspaceId` on every
//     call, after the canvas table already refused any scope key the frame
//     sent (canvas/functions/canvas/tileCalls.js, shared findScopeKey);
//   • records list/get/create/update/upsert/remove only (no subscribe, no
//     schema meta, no integrations).
// Returns null when the project is not installed here: the tile then gets no
// `moduleData` at all.
import { buildModuleDataBridge } from './moduleDataBridge.js'
import { installedAppRefs } from './workspaceConfigs.js'

// `opts.getSdkFn` — tests only (the host calls this; the frame never can).
export function tileModuleDataBridge(project, opts) {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  if (!project || !project.owner || !project.key) return null
  const rs = typeof this.getRootState === 'function' ? this.getRootState() : null
  const ws = rs && rs.workspace
  const wsId = ws && (ws.id || ws._id)
  const settings = ws && ws.settings
  if (!wsId || !settings) return null
  const refs = []
  const mod = settings.workspaceModule
  if (mod && typeof mod === 'object' && mod.owner && mod.key) refs.push(mod)
  refs.push(...installedAppRefs(settings))
  const ref = refs.find((r) => lc(r.owner) === lc(project.owner) && lc(r.key) === lc(project.key))
  const records = ref && ref.capabilities && ref.capabilities.records
  if (!records || !Array.isArray(records.collections) || !records.collections.length) return null
  // A root that holds ONLY this install: the bridge's live-grant sync reads
  // root.workspace.settings, so it can never widen to another app's grant.
  const pinnedRoot = { workspace: { id: String(wsId), settings: { workspaceApps: [ref] } } }
  const bridge = buildModuleDataBridge(
    pinnedRoot,
    { records },
    opts && typeof opts.getSdkFn === 'function' ? { getSdkFn: opts.getSdkFn } : undefined
  )
  const OPS = ['list', 'get', 'create', 'update', 'upsert', 'remove']
  return (from, op, args) => {
    if (from !== 'records' || !OPS.includes(op)) {
      return Promise.reject(new Error('[tile] only records ' + OPS.join('/') + ' are served to a tile.'))
    }
    const a = args && typeof args === 'object' && !Array.isArray(args) ? args : {}
    return bridge('records', op, { ...a, workspaceId: String(wsId) })
  }
}
